import { Icon } from '@amzn/awsui-components-core';
import React, { useMemo, useState } from 'react';
import './WorkDayCalendar.scss';

export interface WorkDayCalendarProps {
  /** Map of date key (YYYY-MM-DD) → work day number */
  workDayMap: Map<string, number>;
  /** Optional title shown above the calendar */
  title?: string;
  /** Optional callback when close button is clicked */
  onClose?: () => void;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const WorkDayCalendar: React.FC<WorkDayCalendarProps> = ({ workDayMap, title = 'MEC Calendar', onClose }) => {
  const [viewDate, setViewDate] = useState(new Date());

  const todayKey = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  const monthDays = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();
    const cells: { day: number; key: string; outside?: boolean }[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const d = prevMonthDays - i;
      const m = month === 0 ? 12 : month;
      const y = month === 0 ? year - 1 : year;
      cells.push({ day: d, key: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`, outside: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ day: d, key });
    }
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        const m = month + 2 > 12 ? 1 : month + 2;
        const y = month + 2 > 12 ? year + 1 : year;
        cells.push({ day: d, key: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`, outside: true });
      }
    }
    return cells;
  }, [viewDate]);

  const monthLabel = viewDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const navigate = (dir: number) => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + dir, 1));
  };

  return (
    <div className="wd-calendar">
      <div className="wd-calendar__header">
        <span className="wd-calendar__title">{title}</span>
        {onClose && (
          <button className="wd-calendar__close" onClick={onClose} aria-label="Close calendar">
            <Icon name="close" />
          </button>
        )}
      </div>
      <div className="wd-calendar__nav">
        <button onClick={() => navigate(-1)} className="wd-calendar__nav-btn" aria-label="Previous month">
          <Icon name="angle-left" />
        </button>
        <span className="wd-calendar__month">{monthLabel}</span>
        <button onClick={() => navigate(1)} className="wd-calendar__nav-btn" aria-label="Next month">
          <Icon name="angle-right" />
        </button>
      </div>
      <div className="wd-calendar__day-names">
        {DAYS.map((d) => (
          <div key={d} className="wd-calendar__day-name">
            {d}
          </div>
        ))}
      </div>
      <div className="wd-calendar__grid">
        {monthDays.map((cell, i) => {
          const wd = workDayMap.get(cell.key);
          const isToday = cell.key === todayKey;
          return (
            <div key={`${cell.key}-${i}`} className={`wd-calendar__cell${cell.outside ? ' wd-calendar__cell--outside' : ''}`}>
              <div className={`wd-calendar__day${isToday ? ' wd-calendar__day--today' : ''}${wd ? ' wd-calendar__day--workday' : ''}`}>
                <span className="wd-calendar__date">{cell.day}</span>
                {wd && <span className="wd-calendar__badge">WD {wd}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkDayCalendar;
